import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { Edit as EditIcon, Check as CheckIcon, Close as CloseIcon, Memory as MemoryIcon } from '@mui/icons-material';
import GenericModal from './GenericModal';
import { ramService } from '../services/api';
import { MarkdownRenderer, markdownLinkComponent } from './MarkdownText';
import { AIRMASK_DARK, AIRMASK_GREEN, AIRMASK_SURFACE_GRAY } from '../constants/colors';
import { useIsCoarsePointer, useMobileKeyboardVisible } from '../hooks/useMobileKeyboardVisible';

interface RAMModalProps {
  open: boolean;
  onClose: () => void;
}

const RAMModal: React.FC<RAMModalProps> = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [content, setContent] = useState('');
  const [draftContent, setDraftContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const isCoarsePointer = useIsCoarsePointer();
  const isMobileKeyboardVisible = useMobileKeyboardVisible();
  const hideEditActionButtons = isCoarsePointer || isMobileKeyboardVisible;
  const isSaveInFlightRef = useRef(false);

  const loadRamContent = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await ramService.getRam();
      if (response.success && response.data) {
        setContent(response.data.content);
      } else {
        setError(response.error || 'Failed to load RAM content');
      }
    } catch {
      setError('Failed to load RAM content');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    setIsEditing(false);
    setDraftContent('');
    setSaveError(null);
    loadRamContent();
  }, [open, loadRamContent]);

  const handleStartEdit = () => {
    setDraftContent(content);
    setSaveError(null);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setDraftContent(content);
    setSaveError(null);
  };

  const handleSave = useCallback(async (): Promise<boolean> => {
    if (isSaveInFlightRef.current) {
      return false;
    }

    if (draftContent === content) {
      setIsEditing(false);
      return true;
    }

    isSaveInFlightRef.current = true;
    setIsSaving(true);
    setSaveError(null);

    try {
      const response = await ramService.updateRam({ content: draftContent });
      if (response.success && response.data) {
        setContent(response.data.content);
        setIsEditing(false);
        return true;
      } else {
        setSaveError(response.error || 'Failed to save RAM content');
      }
    } catch {
      setSaveError('Failed to save RAM content');
    } finally {
      isSaveInFlightRef.current = false;
      setIsSaving(false);
    }

    return false;
  }, [content, draftContent]);

  const handleModalClose = async () => {
    if (!hideEditActionButtons || !isEditing || isSaving || draftContent === content) {
      onClose();
      return;
    }

    const saveSucceeded = await handleSave();
    if (saveSucceeded) {
      onClose();
    }
  };

  const handleDraftBlur = useCallback(() => {
    if (!hideEditActionButtons || !isEditing || isSaving) {
      return;
    }

    // On mobile, the keyboard "Done" checkmark commonly blurs the field.
    // Defer save until the next frame to avoid viewport-resize timing races.
    window.requestAnimationFrame(() => {
      void handleSave();
    });
  }, [hideEditActionButtons, isEditing, isSaving, handleSave]);

  const toggleCheckboxAtIndex = (markdown: string, checkboxIndex: number, checked: boolean): string => {
    let currentIndex = 0;
    const lines = markdown.split('\n');

    const updatedLines = lines.map((line) => {
      const match = line.match(/^(\s*[-*]\s+\[)( |x|X)(\].*)$/);
      if (!match) {
        return line;
      }

      if (currentIndex !== checkboxIndex) {
        currentIndex += 1;
        return line;
      }

      currentIndex += 1;
      return `${match[1]}${checked ? 'x' : ' '}${match[3]}`;
    });

    return updatedLines.join('\n');
  };

  const handlePreviewCheckboxToggle = async (checkboxIndex: number, checked: boolean) => {
    const previousContent = content;
    const updatedContent = toggleCheckboxAtIndex(content, checkboxIndex, checked);

    if (updatedContent === previousContent) {
      return;
    }

    setContent(updatedContent);
    setSaveError(null);

    try {
      const response = await ramService.updateRam({ content: updatedContent });
      if (!response.success || !response.data) {
        setContent(previousContent);
        setSaveError(response.error || 'Failed to save RAM content');
        return;
      }

      setContent(response.data.content);
    } catch {
      setContent(previousContent);
      setSaveError('Failed to save RAM content');
    }
  };

  return (
    <GenericModal
      open={open}
      onClose={handleModalClose}
      ariaLabelledBy="ram-modal-title"
      ariaDescribedBy="ram-modal-description"
    >
      <Box>
        <Box sx={{ position: 'relative', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MemoryIcon sx={{ fontSize: 32 }} />
            <Typography id="ram-modal-title" variant="h4" sx={{ fontWeight: 600 }}>
              RAM
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
            {saveError}
          </Alert>
        )}

        <Box sx={{ position: 'relative', mb: 1, minHeight: 32 }}>
          {/* I might add a title later but right now I want to keep it simple for less cognitive load. */}
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.5 }}>
            {''}
          </Typography>
          {!loading && !isEditing && (
            <IconButton
              size="small"
              aria-label="Edit RAM"
              onClick={handleStartEdit}
              sx={{
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#32CA1D',
                '&:hover': {
                  bgcolor: 'rgba(50, 202, 29, 0.1)',
                },
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        <Box
          id="ram-modal-description"
          sx={{
            p: 2,
            bgcolor: '#1A1A1A',
            borderRadius: 1,
            minHeight: 220,
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 220 }}>
              <CircularProgress size={28} />
            </Box>
          ) : isEditing ? (
            <>
              <TextField
                fullWidth
                multiline
                rows={12}
                value={draftContent}
                onChange={(e) => setDraftContent(e.target.value)}
                onBlur={handleDraftBlur}
                placeholder="Enter markdown"
                disabled={isSaving}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#000',
                    fontFamily: 'monospace',
                    '& fieldset': {
                      borderColor: '#32CA1D',
                    },
                    '&:hover fieldset': {
                      borderColor: '#32CA1D',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#32CA1D',
                    },
                  },
                }}
              />
              {!hideEditActionButtons && (
                <Box display="flex" gap={1} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                    startIcon={<CloseIcon />}
                    sx={{
                      borderColor: 'grey.600',
                      color: 'grey.300',
                      '&:hover': {
                        borderColor: 'grey.500',
                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSave}
                    disabled={isSaving || draftContent === content}
                    startIcon={isSaving ? <CircularProgress size={16} /> : <CheckIcon />}
                    sx={{
                      bgcolor: '#32CA1D',
                      color: '#000',
                      '&:hover': {
                        bgcolor: '#28A518',
                      },
                      '&.Mui-disabled': {
                        bgcolor: 'grey.700',
                        color: 'grey.500',
                      },
                    }}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                </Box>
              )}
            </>
          ) : (
            <Box
              sx={{
                cursor: 'default',
                '& p': {
                  lineHeight: 1.6,
                  mb: 1,
                  fontSize: '1rem',
                },
                '& ul': {
                  pl: 3,
                  mb: 1,
                  listStyleType: 'disc',
                  listStylePosition: 'inside',
                },
                '& ol': {
                  pl: 3,
                  mb: 1,
                  listStyleType: 'decimal',
                  listStylePosition: 'inside',
                },
                '& li': {
                  mb: 0.5,
                },
                '& a': {
                  color: 'primary.main',
                  textDecoration: 'underline',
                },
                '& table': {
                  width: '100%',
                  borderCollapse: 'collapse',
                  mb: 2,
                },
                '& th, & td': {
                  border: '1px solid',
                  borderColor: 'divider',
                  p: 1,
                  textAlign: 'left',
                },
                '& th': {
                  bgcolor: AIRMASK_GREEN,
                  color: AIRMASK_DARK,
                  fontWeight: 600,
                },
                '& code': {
                  bgcolor: AIRMASK_SURFACE_GRAY,
                  color: 'text.primary',
                  px: 0.5,
                  borderRadius: 0.5,
                  fontSize: '0.875em',
                },
                '& pre': {
                  bgcolor: AIRMASK_SURFACE_GRAY,
                  color: 'text.primary',
                  p: 1,
                  borderRadius: 1,
                  overflow: 'auto',
                },
                '& .ram-task-checkbox': {
                  marginRight: 8,
                  cursor: 'pointer',
                },
              }}
            >
              {(() => {
                let checkboxRenderIndex = -1;

                return (
                  <MarkdownRenderer
                    components={{
                      ...markdownLinkComponent,
                      input: ({ type, checked, ...props }: any) => {
                        if (type !== 'checkbox') {
                          return <input type={type} {...props} />;
                        }

                        checkboxRenderIndex += 1;
                        const index = checkboxRenderIndex;

                        return (
                          <input
                            {...props}
                            className="ram-task-checkbox"
                            type="checkbox"
                            checked={Boolean(checked)}
                            disabled={isSaving}
                            onClick={(event) => {
                              event.stopPropagation();
                            }}
                            onChange={(event) => {
                              event.stopPropagation();
                              const checkboxIndex = Number(event.currentTarget.dataset.checkboxIndex);
                              if (!Number.isNaN(checkboxIndex)) {
                                handlePreviewCheckboxToggle(checkboxIndex, event.currentTarget.checked);
                              }
                            }}
                            data-checkbox-index={index}
                          />
                        );
                      },
                    }}
                  >
                    {content}
                  </MarkdownRenderer>
                );
              })()}
            </Box>
          )}
        </Box>
      </Box>
    </GenericModal>
  );
};

export default RAMModal;
